import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../context/auth/AuthContext";
import useTranslator from "../hooks/useTranslator";
import { haveValue, placeNotificationLink, toast } from "../helper/common";
import LandLordContext from "../context/landlord/LandLordContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  calculateNowTime,
  getTenantShareRequests,
} from "../services/sharedService";
import WelcomeModal from "../components/Workspace/WelcomeModal";
import NotificationGroups from "../components/NotificationGroups";
import PendingApplicationsModal from "../components/Workspace/PendingApplicationsModal";
import { useCookies } from "react-cookie";
import TenantContext from "../context/tenant/TenantContext";
import GlobalWorkspaceSearch from "../components/Workspace/GlobalWorkspaceSearch";
import {
  Badge,
  Button,
  Icon,
  Skeleton,
  SkeletonList,
  TextField,
  WorkspaceCard,
} from "../components/design";

// lower case, no accents: "José" matches "jose"
const toSearchText = (value) =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const WorkspaceDashboard = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const loadingRef = useRef(false);
  const notificationsBoxRef = useRef(null);
  const { toggleNewWorkspaceModal, notificationSeen, user, loadUserTenant } =
    authContext;
  const { shareProfile } = useContext(TenantContext);

  const landlordContext = useContext(LandLordContext);
  const {
    fetchAllLandlordNotification,
    fetchWorkspacesOverview,
    acceptWorkspaceInvite,
    getBasicWorkspaceDetails,
  } = landlordContext;

  const [cookies, setCookie, removeCookie] = useCookies([
    "workspace_invite",
    "sharedProfileLink",
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [unreadCount, setUnreadCount] = useState(0);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [workspaceQuery, setWorkspaceQuery] = useState("");
  // false until we know the list: stops the "no workspace yet" screen
  // flashing before the account and the list have loaded
  const [workspacesLoaded, setWorkspacesLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workspaceJoiningLoading, setWorkspaceJoiningLoading] = useState(
    haveValue(cookies.workspace_invite)
  );
  const [allNotifications, setAllNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [total, setTotal] = useState(0);
  const [tempWorkspaceData, setTempWorkspaceData] = useState({});
  const [openWelcomeWorkspaceModal, setOpenWelcomeWorkspaceModal] =
    useState(false);
  const [showPendingApplicationsModal, setShowPendingApplicationsModal] =
    useState(false);
  const [pendingApplicationsData, setPendingApplicationsData] = useState([]);
  const [pendingApplicationsLoading, setPendingApplicationsLoading] =
    useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const importInitiatedRef = useRef(false);

  useEffect(() => {
    const shouldOpen =
      searchParams.get("new_application") === "true" ||
      searchParams.get("new_application") === "1";

    getTenantShareRequests()
      .then((res) => {
        if (shouldOpen) {
          setShowPendingApplicationsModal(true);
        }
        const data =
          res?.success_res?.applications || res?.success_res || res?.data || [];
        setPendingApplicationsData(Array.isArray(data) ? data : []);
        setPendingRequestsCount(Array.isArray(data) ? data.length : 0);
      })
      .catch((err) => {
        console.log(err);
        toast.error(_t("errorWhileFetchingDashboardNotifications"));
      })
      .finally(() => {
        setPendingApplicationsLoading(false);
      });
  }, [searchParams]);

  const closePendingApplicationsModal = () => {
    setShowPendingApplicationsModal(false);
    const nextSearch = new URLSearchParams(searchParams);
    nextSearch.delete("new_application");
    setSearchParams(nextSearch, { replace: true });
  };

  const loadMoreNotifications = () => {
    if (!loadingRef.current && allNotifications.length < total) {
      loadData(page + 1);
    }
  };

  // On wide screens the notifications box scrolls on its own (the page has a
  // fixed height); on smaller screens the whole page scrolls. Check both.
  const handleNotificationsScroll = (e) => {
    const box = e.currentTarget;
    if (box.scrollHeight - box.scrollTop - box.clientHeight < 300) {
      loadMoreNotifications();
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 500
      ) {
        loadMoreNotifications();
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, total, allNotifications.length]);

  // a tall screen may show the whole first page without a scrollbar,
  // so nothing would ever scroll: load the next page straight away
  useEffect(() => {
    const box = notificationsBoxRef.current;
    if (
      box &&
      allNotifications.length > 0 &&
      box.scrollHeight <= box.clientHeight
    ) {
      loadMoreNotifications();
    }
  }, [allNotifications.length, total]);

  const loadData = async (currentPage) => {
    try {
      setLoading(true);
      if (loadingRef.current) return;
      loadingRef.current = true;

      const notificationRes = await fetchAllLandlordNotification(currentPage);

      setAllNotifications((prev) => [
        ...prev,
        ...(notificationRes?.notifications || []),
      ]);
      setUnreadCount(notificationRes?.unread_notifications || 0);
      setTotal(notificationRes?.total_notifications);
      setPage(notificationRes?.current_page);
    } catch (err) {
      console.log(err, "");
      toast.error(_t("errorWhileFetchingDashboardNotifications"));
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const importProfileRedirect = async () => {
    if (importInitiatedRef.current) return;

    let userid = localStorage.getItem("saveduserid");
    const sharedProfileLink = cookies.sharedProfileLink;

    if (haveValue(sharedProfileLink)) {
      importInitiatedRef.current = true;
      const shareProfileResponse = await shareProfile(
        sharedProfileLink,
        userid,
        "null",
        "full",
        { shareType: "tLink" }
      );
      removeCookie("sharedProfileLink", { path: "/" });

      if (shareProfileResponse?.success) {
        toast.success(_t("profileImported"));
      } else if (shareProfileResponse?.key === "profile-not-found") {
        toast.error(shareProfileResponse?.errormsg ?? _t("failedToGetProfile"));
        return;
      } else if (shareProfileResponse?.key === "access-cancelled") {
        toast.error(
          _t(shareProfileResponse?.errormsg) ?? _t("failedToGetProfile")
        );
        return;
      }
    }
  };

  useEffect(() => {
    if (haveValue(cookies.workspace_invite)) {
      setWorkspaceJoiningLoading(true);
      const temp_workspace_id = cookies.workspace_invite;
      acceptWorkspaceInvite({ workspaceId: temp_workspace_id })
        .then((res) => {
          console.log("Workspace invite accepted.");
          removeCookie("workspace_invite", {
            path: "/", // ✅ MUST MATCH,
            maxAge: 1800,
          });
          getBasicWorkspaceDetails({ workspaceId: temp_workspace_id }).then(
            (res) => {
              const data = res?.success_res?.data;
              setTempWorkspaceData(data);
              setOpenWelcomeWorkspaceModal(true);
            }
          );
          loadUserTenant("landlord");
        })
        .catch((err) => {
          console.log(err, "Error while accepting workspace invite");
        })
        .finally(() => {
          setWorkspaceJoiningLoading(false);
        });
    }
    importProfileRedirect();

    getTenantShareRequests()
      .then((res) => {
        const data =
          res?.success_res?.applications || res?.success_res || res?.data || [];
        setPendingRequestsCount(Array.isArray(data) ? data.length : 0);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if ((user?.workspaceIds ?? [])?.length === 0) {
      // account loaded and it really has no workspaces
      if (user?._id) setWorkspacesLoaded(true);
      return;
    }
    loadData();
    Promise.all([fetchWorkspacesOverview()])
      .then(([workspaceRes]) => {
        setAllWorkspaces(workspaceRes?.success_res);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setWorkspacesLoaded(true));
  }, [user?._id, user?.workspaceIds?.length]);

  if (
    workspacesLoaded &&
    !(loading || workspaceJoiningLoading) &&
    allWorkspaces?.length === 0
  ) {
    return (
      <div className="workspace-dashboard-main workspace-dashboard-empty">
        <div className="workspace-empty-state">
          <div className="workspace-empty-icon">
            <img src="/images/briefcaseIcon.svg" alt="workspace" />
          </div>
          <h4>{_t("youDontHaveAWorkspaceYet")}</h4>
          <p>
            {_t(
              "createAWorkspaceToInviteTenantsReviewProfilesAndManageYourProperties"
            )}
          </p>
          <Button
            variant="outline"
            iconStart={<Icon name="plus" size={16} />}
            onClick={() => toggleNewWorkspaceModal(true)}
          >
            {_t("createYourWorkspace")}
          </Button>
        </div>
      </div>
    );
  }

  const visibleWorkspaces = allWorkspaces.filter((workspace) =>
    toSearchText(workspace?.name).includes(toSearchText(workspaceQuery.trim()))
  );

  return (
    <div className="workspace-dashboard-main">
      <aside className="all-workspace-container workspace-dashboard-sidebar">
        <div className="d-flex align-items-start justify-content-between add-workspace-wrapper">
          <div className="all-workspace-text-wrapper">
            <h4>{_t("myWorkspaces")}</h4>
            {allWorkspaces.length > 0 ? (
              <p>
                {allWorkspaces.length} {_t("workspaces")}
              </p>
            ) : (
              <Skeleton width={90} height={12} radius="pill" />
            )}
          </div>
          <Button
            className="workspace-add-button"
            iconOnly
            iconStart={<Icon name="plus" size={20} />}
            aria-label={_t("addNewWorkspace")}
            title={_t("addNewWorkspace")}
            onClick={() => toggleNewWorkspaceModal(true)}
          />
        </div>

        <TextField
          id="workspace-search"
          className="workspace-search-field"
          icon={<Icon name="search" />}
          placeholder={_t("searchWorkspaces")}
          aria-label={_t("searchWorkspaces")}
          value={workspaceQuery}
          onChange={(e) => setWorkspaceQuery(e.target.value)}
          trailing={
            workspaceQuery && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={_t("clearSearch")}
                onClick={() => setWorkspaceQuery("")}
              >
                <Icon name="close" size={16} />
              </Button>
            )
          }
        />

        <div className="workspace-info-card-wrapper">
          {/* the empty state replaces the page once loaded, so an empty
              list here means the workspaces are still loading */}
          {allWorkspaces.length === 0 && (
            <SkeletonList
              rows={3}
              variant="card"
              avatarSize={36}
              label={_t("loading")}
            />
          )}
          {allWorkspaces.length > 0 && visibleWorkspaces.length === 0 && (
            <p className="workspace-search-empty">{_t("noWorkspacesFound")}</p>
          )}
          {visibleWorkspaces.map((workspace) => (
            <WorkspaceCard
              key={workspace?._id}
              name={workspace?.name}
              image={
                haveValue(workspace?.workspaceImage)
                  ? workspace?.workspaceImage
                  : undefined
              }
              badge={
                workspace?._id?.toString() ===
                  user?.workspaceIds?.[0]?._id?.toString() &&
                user?.workspaceIds?.[0]?.created_by?.toString() ===
                  user?._id?.toString() ? (
                  <Badge>{_t("default")}</Badge>
                ) : null
              }
              lines={[
                <>
                  {_t("memberCount", "", "translation", {
                    count: workspace?.memberCount ?? 0,
                  })}
                  {workspace?.lastOpenedAt && (
                    <>
                      {" · "}
                      {_t("activeTimeAgo", "", "translation", {
                        time: calculateNowTime(
                          workspace?.lastOpenedAt,
                          localStorage.getItem("i18nextLng")
                        ),
                      })}
                    </>
                  )}
                </>,
              ]}
              onClick={() => {
                loadUserTenant("landlord");
                navigate(`/${workspace?.uuid}/profile-request`);
              }}
            />
          ))}
        </div>
      </aside>

      <main className="workspace-dashboard-content">
        <GlobalWorkspaceSearch workspaces={allWorkspaces} />

        <section
          ref={notificationsBoxRef}
          className="all-workspace-container notification-container"
          onScroll={handleNotificationsScroll}
        >
          <div className="d-flex align-items-center justify-content-between notification-heading">
            <div className="d-flex align-items-center custom-gap-3">
              <div className="all-workspace-icon-wrapper d-flex align-items-center justify-content-center">
                <img src="/images/bell-icon-black.svg" alt="" />
              </div>
              <div className="all-workspace-text-wrapper">
                <h4>{_t("notifications")}</h4>
              </div>
              {!(loading && allNotifications.length === 0) && (
                <Badge>
                  {unreadCount} {_t("unread")}
                </Badge>
              )}
            </div>
          </div>

          {allNotifications.length > 0 ? (
            <NotificationGroups
              notifications={allNotifications}
              getAvatar={(notification) =>
                `${notification?.created_by_tenant?.fname?.[0] ?? ""}${
                  notification?.created_by_tenant?.lname?.[0] ?? ""
                }` || notification?.workspaceId?.name?.[0]
              }
              getMeta={(notification) => (
                <div className="d-flex align-items-center workspace-name-wrapper custom-gap-2">
                  {haveValue(notification?.workspaceId?.workspaceImage) ? (
                    <img
                      src={`${notification?.workspaceId?.workspaceImage}`}
                      alt=""
                    />
                  ) : (
                    notification?.workspaceId?.name && (
                      <h5 className="d-flex align-items-center justify-content-center">
                        {notification?.workspaceId?.name?.substring(0, 1)}
                      </h5>
                    )
                  )}
                  {notification?.workspaceId?.name && (
                    <>
                      <h4>{notification?.workspaceId?.name}</h4>
                      <span className="notification-meta-dot" />
                    </>
                  )}
                  <p>
                    {calculateNowTime(
                      notification?.createdAt,
                      localStorage.getItem("i18nextLng")
                    )}
                  </p>
                </div>
              )}
              onOpen={(notification) => {
                if (notification?.shared_profile_id?.pendingDecision === true) {
                  toast.error(_t("pendingDecisionFromTenant"));
                  return;
                }
                notificationSeen(notification?._id);
                navigate(
                  placeNotificationLink(
                    notification,
                    notification?.workspaceId?.uuid
                  )
                );
              }}
            />
          ) : (
            !loading && <div className="noData">{_t("noDataFound")}</div>
          )}
          {(loading || workspaceJoiningLoading) && (
            <SkeletonList
              rows={allNotifications.length ? 2 : 4}
              variant="card"
              className="notification-skeleton"
              label={_t("loading")}
            />
          )}
          {openWelcomeWorkspaceModal && (
            <WelcomeModal
              isOpen={openWelcomeWorkspaceModal}
              onClose={() => setOpenWelcomeWorkspaceModal(false)}
              workspaceData={tempWorkspaceData}
            />
          )}
          {/* <PendingApplicationsModal
          isOpen={showPendingApplicationsModal}
          onClose={closePendingApplicationsModal}
          loading={pendingApplicationsLoading}
          applications={pendingApplicationsData}
          workspaces={allWorkspaces}
          /> */}
        </section>
      </main>
    </div>
  );
};

export default WorkspaceDashboard;
